/* The archive: 214 sets, one faceted wall of them.
 *
 * EVERY FILTER LIVES IN THE URL. Not in useState, not in a context, not in
 * sessionStorage — in the query string, read with useSearchParams and written
 * back on every change. That is what makes a filtered view a thing you can send
 * someone, and it is what makes the back button undo a filter instead of
 * leaving the page. An archive whose state is invisible to the address bar is
 * an archive nobody can quote.
 *
 * `/genre/:slug` IS THIS PAGE with one chip already pressed. The route is
 * prettier and links better; the filters and the grid behind it are the same
 * object. Selecting a second genre widens to `/archive?genre=a&genre=b`, and
 * narrowing back to one genre with nothing else on returns to the pretty URL.
 *
 * ONE CARD SPANS TWO COLUMNS, and it is always a staff pick. That is the single
 * deliberate break in the grid and it is justified informationally rather than
 * decoratively: a staff pick carries an editor's note that no other card has, so
 * it is the only card with something extra to say. The break is earned by the
 * content, not spent on making the page look less regular.
 *
 * PLAY ALL RUNS THROUGH THE LOADED SET. There is one <audio> element on this
 * station and it lives above the router, so a run-through is bookkeeping here,
 * not a second player: we hand the persistent player one episode, watch for it
 * to reach the end, and hand it the next one. SoundCloud is preferred when an
 * episode has a public track URL; legacy local audio remains playable during
 * the cutover. Leaving this page ends the run-through, and the bar says so while
 * it is going, with a stop button — never a hidden autoplay.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import type { EpisodeQuery } from '../api';
import {
  Artwork,
  Chip,
  ChipList,
  Empty,
  EpisodeCardLink,
  ErrorNote,
  Section,
  Spinner,
  hostNames,
  shortDate,
  useNarrow,
} from '../components';
import { formatDuration, readTzMode, stationToday } from '../format';
import { usePlayer } from '../player';
import type { EpisodeCard, EpisodeSort, GenreOut } from '../types';

/** The API's own default page size, and four rows of the desktop grid. */
const PAGE_SIZE = 24;

/** The oldest public set currently on the official SoundCloud profile. */
const STATION_FIRST_YEAR = 2021;

/** Genre chips drawn before the row folds into a `+N`. The design's count. */
const GENRE_CHIPS_SHOWN = 11;

/* --------------------------------------------------------------- filters */

type Who = 'residents' | 'guests';
type Format = 'live' | 'prerecorded';

interface Filters {
  genres: string[];
  staffPick: boolean;
  who: Who | null;
  format: Format | null;
  tracklist: boolean;
  year: number | null;
  q: string | null;
  sort: EpisodeSort;
}

/**
 * The query string, plus the genre in the path when we arrived at
 * `/genre/:slug`. The route param is folded into the same list as the query
 * genres so that nothing downstream has to know which door the visitor came in
 * through.
 */
function readFilters(params: URLSearchParams, routeGenre: string | undefined): Filters {
  const genres = new Set<string>();
  if (routeGenre) genres.add(routeGenre.trim().toLowerCase());
  for (const value of params.getAll('genre')) {
    const slug = value.trim().toLowerCase();
    if (slug) genres.add(slug);
  }

  const who = params.get('who');
  const format = params.get('format');
  const sort = params.get('sort');
  const year = Number(params.get('year'));

  return {
    genres: [...genres],
    staffPick: params.get('pick') === '1',
    who: who === 'residents' || who === 'guests' ? who : null,
    format: format === 'live' || format === 'prerecorded' ? format : null,
    tracklist: params.get('tracklist') === '1',
    year: Number.isInteger(year) && year >= STATION_FIRST_YEAR ? year : null,
    q: (params.get('q') ?? '').trim() || null,
    sort: sort === 'oldest' || sort === 'staff' ? sort : 'newest',
  };
}

function isDefault(filters: Filters): boolean {
  return (
    filters.genres.length === 0 &&
    !filters.staffPick &&
    filters.who === null &&
    filters.format === null &&
    !filters.tracklist &&
    filters.year === null &&
    filters.q === null &&
    filters.sort === 'newest'
  );
}

/** Everything except the sort, which is a view preference rather than a filter. */
function hasFilter(filters: Filters): boolean {
  return !isDefault({ ...filters, sort: 'newest' });
}

/**
 * Filters -> the address to navigate to.
 *
 * One genre and nothing else goes back to `/genre/:slug`: that URL is the one
 * we put in the chips, in the cards and in other people's links, and a page that
 * quietly rewrote it to `?genre=` every time you pressed something would leave
 * two addresses for one view.
 */
function toPath(filters: Filters): string {
  const onlyGenre =
    filters.genres.length === 1 && isDefault({ ...filters, genres: [] }) ? filters.genres[0] : null;
  if (onlyGenre) return `/genre/${encodeURIComponent(onlyGenre)}`;

  const params = new URLSearchParams();
  for (const slug of filters.genres) params.append('genre', slug);
  if (filters.staffPick) params.set('pick', '1');
  if (filters.who) params.set('who', filters.who);
  if (filters.format) params.set('format', filters.format);
  if (filters.tracklist) params.set('tracklist', '1');
  if (filters.year !== null) params.set('year', String(filters.year));
  if (filters.q) params.set('q', filters.q);
  if (filters.sort !== 'newest') params.set('sort', filters.sort);

  const encoded = params.toString();
  return encoded ? `/archive?${encoded}` : '/archive';
}

function toQuery(filters: Filters): EpisodeQuery {
  return {
    genre: filters.genres.length > 0 ? filters.genres : undefined,
    staff_pick: filters.staffPick ? true : undefined,
    resident: filters.who === null ? undefined : filters.who === 'residents',
    format: filters.format ?? undefined,
    has_tracklist: filters.tracklist ? true : undefined,
    year: filters.year ?? undefined,
    q: filters.q ?? undefined,
    sort: filters.sort,
  };
}

/* ----------------------------------------------------------------- styles */

const HEAD: CSSProperties = {
  display: 'block',
  fontSize: 'clamp(2.75rem, 8vw, 4.75rem)',
  lineHeight: 0.92,
  letterSpacing: '-.015em',
  fontVariationSettings: "'wdth' 118, 'wght' 800",
};

const INTRO: CSSProperties = {
  maxWidth: '46ch',
  fontSize: '0.9375rem',
  color: 'var(--muted)',
};

const FILTERS: CSSProperties = {
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--paper)',
};

const FROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '14px clamp(12px, 4vw, 28px)',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  flexWrap: 'wrap',
};

const FLABEL: CSSProperties = {
  width: '96px',
  flexShrink: 0,
  color: 'var(--muted)',
  paddingTop: '9px',
};

const BAR: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'clamp(10px, 2vw, 20px)',
  padding: '12px clamp(12px, 4vw, 28px)',
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--blue)',
  color: 'var(--on-blue)',
  flexWrap: 'wrap',
};

const BAR_COUNT: CSSProperties = {
  fontSize: 'clamp(1.375rem, 3vw, 1.875rem)',
  lineHeight: 1,
  fontVariationSettings: "'wdth' 100, 'wght' 800",
  textTransform: 'uppercase',
};

const GRID_PAD: CSSProperties = { paddingTop: '26px' };

const TAIL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexWrap: 'wrap',
  padding: '30px clamp(12px, 4vw, 28px) 40px',
};

const FEATURE_BODY: CSSProperties = {
  justifyContent: 'flex-end',
  gap: '11px',
  padding: '18px',
  minWidth: 0,
};

function barButton(selected: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '44px',
    padding: '0 12px',
    fontFamily: 'var(--ff-mono)',
    fontWeight: 500,
    fontSize: 'var(--fs-meta)',
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    background: selected ? 'var(--paper)' : 'transparent',
    color: selected ? 'var(--text)' : 'var(--on-blue)',
    boxShadow: selected ? 'inset 0 0 0 var(--bw-chip) var(--edge)' : 'none',
    textDecoration: selected ? 'none' : 'underline',
    textUnderlineOffset: '3px',
  };
}

/* ------------------------------------------------------------------- page */

type ListState = 'loading' | 'ready' | 'error';

export default function Archive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug: routeGenre } = useParams<{ slug?: string }>();
  const [tz] = useState(readTzMode);
  const oneColumn = useNarrow(620);

  const filters = useMemo(
    () => readFilters(searchParams, routeGenre),
    [searchParams, routeGenre],
  );

  /* -------------------------------------------------------------- genres */

  const [genres, setGenres] = useState<GenreOut[] | null>(null);
  const [genresError, setGenresError] = useState<unknown>(null);
  const [genresAttempt, setGenresAttempt] = useState(0);
  const [genresExpanded, setGenresExpanded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setGenresError(null);
    api
      .getGenres(controller.signal)
      .then(setGenres)
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setGenresError(error);
      });
    return () => controller.abort();
  }, [genresAttempt]);

  /* ------------------------------------------------------------ episodes */

  const [items, setItems] = useState<EpisodeCard[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<ListState>('loading');
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const moreRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // A filter change invalidates whatever "load more" was fetching for the
    // previous filters; appending that page would mix two result sets.
    moreRef.current?.abort();
    moreRef.current = null;
    setMoreLoading(false);
    setMoreError(null);

    setState('loading');
    setError(null);

    api
      .getEpisodes({ ...toQuery(filters), limit: PAGE_SIZE, offset: 0 }, controller.signal)
      .then((page) => {
        setItems(page.results);
        setTotal(page.total);
        setState('ready');
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
        setState('error');
      });

    return () => controller.abort();
  }, [filters, attempt]);

  useEffect(() => () => moreRef.current?.abort(), []);

  const loadMore = useCallback(() => {
    moreRef.current?.abort();
    const controller = new AbortController();
    moreRef.current = controller;
    setMoreLoading(true);
    setMoreError(null);

    api
      .getEpisodes(
        { ...toQuery(filters), limit: PAGE_SIZE, offset: items.length },
        controller.signal,
      )
      .then((page) => {
        setTotal(page.total);
        setItems((current) => {
          const seen = new Set(current.map((episode) => episode.catalogue_no));
          return [...current, ...page.results.filter((e) => !seen.has(e.catalogue_no))];
        });
        setMoreLoading(false);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setMoreError(failure);
        setMoreLoading(false);
      });
  }, [filters, items.length]);

  /* -------------------------------------------------------------- player */

  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** The catalogue number the run-through is currently on. Null when idle. */
  const [running, setRunning] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [playNote, setPlayNote] = useState<string | null>(null);
  const startRef = useRef<AbortController | null>(null);

  useEffect(() => () => startRef.current?.abort(), []);

  /**
   * Hand the player the first playable episode in `list`. `run` says whether
   * finishing it should pull in the next one.
   */
  const start = useCallback(async (list: readonly string[], run: boolean) => {
    startRef.current?.abort();
    const controller = new AbortController();
    startRef.current = controller;

    setPlayNote(null);
    setStarting(true);

    let skipped = 0;
    for (const catalogueNo of list) {
      try {
        const episode = await api.getEpisode(catalogueNo, controller.signal);
        if (!episode.soundcloud_url && !episode.audio_url) {
          skipped += 1;
          continue;
        }
        playerRef.current.playEpisode(episode);
        setRunning(run ? catalogueNo : null);
        setStarting(false);
        if (episode.soundcloud_url) {
          setPlayNote(
            `${catalogueNo} is loading its SoundCloud player without starting audio. Press Play again when the player says it is ready.`,
          );
        } else if (skipped > 0) {
          setPlayNote(
            `Skipped ${skipped} ${skipped === 1 ? 'set' : 'sets'} with no SoundCloud track or local replay attached.`,
          );
        }
        return;
      } catch (failure: unknown) {
        if (isAbortError(failure)) return;
        skipped += 1;
      }
    }

    setRunning(null);
    setStarting(false);
    setPlayNote(
      list.length === 0
        ? 'There is nothing here to play.'
        : 'None of these sets has a SoundCloud track or local replay attached yet.',
    );
  }, []);

  /* The queue, one hop at a time. We do not touch either transport: when the
     current episode ends we hand the player the next item on screen. Local
     audio can continue immediately; a newly keyed SoundCloud player stops in
     its configured-ready state and asks for the next explicit Play press. */
  useEffect(() => {
    if (!running || starting) return;
    if (player.mode !== 'episode') return;
    if (player.current?.catalogue_no !== running) return;
    if (!player.episodeEnded) return;

    const index = itemsRef.current.findIndex((e) => e.catalogue_no === running);

    // The filters moved while it was playing, so the set that just finished is
    // no longer on this page. There is no sensible "next" from a list it is not
    // in, so the run-through ends here and says which of the two things happened.
    if (index < 0) {
      setRunning(null);
      setPlayNote('The filters changed while that set was playing, so we stopped at the end of it.');
      return;
    }

    const rest = itemsRef.current.slice(index + 1).map((e) => e.catalogue_no);
    if (rest.length === 0) {
      setRunning(null);
      setPlayNote('That was the last set in this list.');
      return;
    }
    void start(rest, true);
  }, [
    running,
    starting,
    player.mode,
    player.current,
    player.episodeEnded,
    start,
  ]);

  const playAll = useCallback(() => {
    void start(
      itemsRef.current.map((episode) => episode.catalogue_no),
      true,
    );
  }, [start]);

  const playOne = useCallback(
    (episode: EpisodeCard) => {
      const current = playerRef.current;
      if (current.mode === 'episode' && current.current?.catalogue_no === episode.catalogue_no) {
        current.toggle();
        return;
      }
      // Pressing one card is a choice about one set; it ends the run-through
      // rather than silently re-pointing it.
      setRunning(null);
      void start([episode.catalogue_no], false);
    },
    [start],
  );

  const isLoaded = useCallback(
    (episode: EpisodeCard) =>
      player.mode === 'episode' &&
      player.current?.catalogue_no === episode.catalogue_no &&
      player.isPlaying,
    [player],
  );

  /* --------------------------------------------------------------- title */

  const selectedGenreNames = useMemo(() => {
    if (!genres) return filters.genres;
    return filters.genres.map(
      (slug) => genres.find((genre) => genre.slug === slug)?.name ?? slug,
    );
  }, [genres, filters.genres]);

  useEffect(() => {
    const scope = selectedGenreNames.length > 0 ? `${selectedGenreNames.join(' + ')} — ` : '';
    document.title = `${scope}Archive — Higher Ground Radio`;
  }, [selectedGenreNames]);

  /* ------------------------------------------------------------ mutation */

  const apply = useCallback(
    (next: Filters) => {
      navigate(toPath(next));
    },
    [navigate],
  );

  const toggleGenre = useCallback(
    (slug: string) => {
      const on = filters.genres.includes(slug);
      apply({
        ...filters,
        genres: on ? filters.genres.filter((g) => g !== slug) : [...filters.genres, slug],
      });
    },
    [apply, filters],
  );

  /* ---------------------------------------------------------------- view */

  const years = useMemo(() => {
    const thisYear = Number(stationToday().slice(0, 4));
    const last = Number.isFinite(thisYear) ? Math.max(thisYear, STATION_FIRST_YEAR) : STATION_FIRST_YEAR;
    const out: number[] = [];
    for (let year = last; year >= STATION_FIRST_YEAR; year -= 1) out.push(year);
    return out;
  }, []);

  const visibleGenres = useMemo(() => {
    if (!genres) return [];
    // A chip that returns nothing when you press it is worse than a chip that
    // is not there — the count decides. A selected genre is always drawn, even
    // at zero, so a filter can always be taken off again.
    return genres.filter((genre) => genre.episode_count > 0 || filters.genres.includes(genre.slug));
  }, [genres, filters.genres]);

  const genreChips = genresExpanded ? visibleGenres : visibleGenres.slice(0, GENRE_CHIPS_SHOWN);
  const genresHidden = visibleGenres.length - genreChips.length;

  /* The grid break. The first staff pick in the loaded set becomes the wide
     card and is taken out of the ordinary run so it is not drawn twice. */
  const feature = useMemo(() => items.find((episode) => episode.is_staff_pick) ?? null, [items]);
  const rest = useMemo(
    () => (feature ? items.filter((episode) => episode.catalogue_no !== feature.catalogue_no) : items),
    [items, feature],
  );

  const remaining = Math.max(0, total - items.length);
  const countWord = total === 1 ? '1 result' : `${total} results`;

  return (
    <>
      <Section level={1} title={<span style={HEAD}>Archive</span>} meta={null}>
        <p className="t-body" style={INTRO}>
          Every public set on Higher Ground Radio's SoundCloud, with its original cover and notes.
          Streamed, never downloadable; playback stays with SoundCloud while the searchable archive
          lives here.
        </p>
      </Section>

      <div style={FILTERS}>
        <div style={FROW}>
          <div className="mono" style={FLABEL} id="filter-genre">
            Genre
          </div>
          {genresError ? (
            /* The genre row failing does not stop the archive: the grid below
               still loads, so this says what is missing and offers the one move
               that fixes it rather than taking the whole page down with it. */
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <span className="mono" role="alert">
                The genre list did not load
              </span>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setGenresAttempt((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          ) : !genres ? (
            <Spinner label="Loading the genres" />
          ) : visibleGenres.length === 0 ? (
            <span className="mono dim">No genres have been broadcast yet.</span>
          ) : (
            <ChipList label="Genre">
              {genreChips.map((genre) => {
                const selected = filters.genres.includes(genre.slug);
                return (
                  <Chip
                    key={genre.slug}
                    color={selected ? 'sky' : 'paper'}
                    count={genre.episode_count}
                    selected={selected}
                    onPress={() => toggleGenre(genre.slug)}
                  >
                    {genre.name}
                  </Chip>
                );
              })}
              {genresHidden > 0 ? (
                <Chip onPress={() => setGenresExpanded(true)}>+{genresHidden}</Chip>
              ) : null}
              {genresExpanded && visibleGenres.length > GENRE_CHIPS_SHOWN ? (
                <Chip onPress={() => setGenresExpanded(false)}>Fewer</Chip>
              ) : null}
            </ChipList>
          )}
        </div>

        <div style={FROW}>
          <div className="mono" style={FLABEL}>
            Filter
          </div>
          <ChipList label="Filters">
            <Chip
              selected={filters.staffPick}
              onPress={() => apply({ ...filters, staffPick: !filters.staffPick })}
            >
              Staff picks
            </Chip>
            <Chip
              selected={filters.who === 'residents'}
              onPress={() =>
                apply({ ...filters, who: filters.who === 'residents' ? null : 'residents' })
              }
            >
              Rhythm Police only
            </Chip>
            <Chip
              selected={filters.who === 'guests'}
              onPress={() => apply({ ...filters, who: filters.who === 'guests' ? null : 'guests' })}
            >
              Guests
            </Chip>
            <Chip
              selected={filters.format === 'prerecorded'}
              onPress={() =>
                apply({
                  ...filters,
                  format: filters.format === 'prerecorded' ? null : 'prerecorded',
                })
              }
            >
              Pre-recorded
            </Chip>
            <Chip
              selected={filters.format === 'live'}
              onPress={() =>
                apply({ ...filters, format: filters.format === 'live' ? null : 'live' })
              }
            >
              Live in the booth
            </Chip>
            <Chip
              selected={filters.tracklist}
              onPress={() => apply({ ...filters, tracklist: !filters.tracklist })}
            >
              Has tracklist
            </Chip>
            {years.map((year) => (
              <Chip
                key={year}
                selected={filters.year === year}
                onPress={() => apply({ ...filters, year: filters.year === year ? null : year })}
              >
                {year}
              </Chip>
            ))}
            {/* Not a control we draw a box for — there is no search field in
                this design — but `?q=` is a real filter the API supports and
                other surfaces link to it, so a search arriving in the URL is
                shown as an active filter that can be taken off again. */}
            {filters.q ? (
              <Chip
                selected
                ariaLabel={`Remove the search for ${filters.q}`}
                onPress={() => apply({ ...filters, q: null })}
              >
                Search: {filters.q} ×
              </Chip>
            ) : null}
          </ChipList>
        </div>
      </div>

      <div className="on-blue" style={BAR}>
        <div style={BAR_COUNT}>
          {state === 'loading' ? 'Counting…' : state === 'error' ? 'Archive unavailable' : countWord}
        </div>

        <div
          role="group"
          aria-label="Sort the results"
          style={{ display: 'flex', flexWrap: 'wrap' }}
        >
          <span className="mono" style={{ alignSelf: 'center', paddingRight: '10px' }}>
            Sorted by
          </span>
          {(
            [
              ['newest', 'Newest'],
              ['oldest', 'Oldest'],
              ['staff', 'Staff picks first'],
            ] as [EpisodeSort, string][]
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filters.sort === value}
              style={barButton(filters.sort === value)}
              onClick={() => apply({ ...filters, sort: value })}
            >
              {label}
            </button>
          ))}
        </div>

        {hasFilter(filters) ? (
          <Link className="mono" to="/archive" style={{ ...barButton(false), color: 'var(--on-blue)' }}>
            Reset filters
          </Link>
        ) : null}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {running ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setRunning(null);
                setPlayNote('Stopped after this set.');
              }}
            >
              Queue active — stop
            </button>
          ) : (
            <button
              type="button"
              className="btn"
              style={{ display: 'flex', alignItems: 'center', gap: '11px' }}
              disabled={items.length === 0 || starting}
              onClick={playAll}
              aria-label={`Queue the ${items.length} sets loaded on this page, newest first`}
            >
              <span className="tri" aria-hidden="true" />
              {starting ? 'Loading first…' : `Queue all ${items.length}`}
            </button>
          )}
        </div>
      </div>

      {playNote ? (
        <p
          className="mono dim"
          role="status"
          style={{ margin: 0, padding: '12px clamp(12px, 4vw, 28px)', lineHeight: 1.6 }}
        >
          {playNote}
        </p>
      ) : null}

      {state === 'error' ? (
        <ErrorNote
          error={error}
          what="the archive"
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : state === 'loading' ? (
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading the archive" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          kind="No matches"
          title="Nothing in the archive matches those filters."
          actions={
            <>
              <Link className="btn" to="/archive">
                Clear the filters
              </Link>
              <Link className="btn-ghost" to="/residents">
                Browse by host
              </Link>
            </>
          }
        >
          <p className="t-body">
            Some combinations have simply never happened — a live gospel-house set with a tracklist
            from 2026 is a narrow ask on a station this size. Take one chip off and try again.
          </p>
        </Empty>
      ) : (
        <>
          <div className="cardgrid" style={GRID_PAD}>
            {feature ? (
              <div
                className="cardwrap on-ink"
                style={{ gridColumn: oneColumn ? 'auto' : 'span 2' }}
              >
                {/* A <div> with the title carrying one stretched link — same
                    reason as EpisodeCardLink: the genre chips below are links,
                    and a link inside a link is invalid HTML that browsers
                    repair differently, so the chips stop working on some of
                    them. */}
                <div
                  className="card"
                  style={{
                    flexDirection: oneColumn ? 'column' : 'row',
                    background: 'var(--ink)',
                    color: 'var(--on-ink)',
                  }}
                >
                  <Artwork
                    color="ink"
                    imageUrl={feature.artwork_url}
                    catalogueNo={feature.catalogue_no}
                    label="Staff pick"
                    size="card"
                    style={
                      oneColumn
                        ? { borderBottom: 'var(--bw) solid var(--edge)' }
                        : {
                            width: '50%',
                            flexShrink: 0,
                            aspectRatio: 'auto',
                            borderBottom: 0,
                            borderRight: 'var(--bw) solid var(--edge)',
                          }
                    }
                  />
                  <div className="card__body" style={FEATURE_BODY}>
                    <div className="mono" style={{ color: 'var(--accent)' }}>
                      Staff pick
                    </div>
                    <h3 style={{ fontSize: 'clamp(1.5rem, 3.4vw, 2.125rem)', lineHeight: 0.98 }}>
                      <Link
                        className="card__link"
                        to={`/episodes/${encodeURIComponent(feature.catalogue_no)}`}
                      >
                        {feature.title}
                      </Link>
                    </h3>
                    {hostNames(feature.hosts) ? (
                      <div className="mono" style={{ color: 'var(--on-ink)' }}>
                        {hostNames(feature.hosts)}
                      </div>
                    ) : null}
                    <div className="mono" style={{ color: 'var(--on-ink)' }}>
                      {feature.catalogue_no} · {shortDate(feature.broadcast_at, tz)} ·{' '}
                      {formatDuration(feature.duration_seconds)}
                    </div>
                    {feature.staff_pick_note ? (
                      <p
                        className="t-body"
                        style={{ fontSize: '0.9375rem', color: 'var(--on-ink)', maxWidth: '52ch' }}
                      >
                        {feature.staff_pick_note}
                      </p>
                    ) : null}
                    {feature.genres.length > 0 ? (
                      <ChipList label="Genres">
                        {feature.genres.map((genre) => (
                          <Chip key={genre.slug} color={genre.color_token} to={`/genre/${genre.slug}`}>
                            {genre.name}
                          </Chip>
                        ))}
                      </ChipList>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className="pbtn card__play"
                  aria-label={`${isLoaded(feature) ? 'Pause' : 'Play'} ${feature.title}, ${feature.catalogue_no}`}
                  aria-pressed={isLoaded(feature)}
                  onClick={() => playOne(feature)}
                >
                  <span className={isLoaded(feature) ? 'pausebars' : 'tri'} aria-hidden="true" />
                </button>
              </div>
            ) : null}

            {rest.map((episode) => (
              <EpisodeCardLink
                key={episode.catalogue_no}
                episode={episode}
                tz={tz}
                onPlay={playOne}
                isPlaying={isLoaded(episode)}
                genreHref={(slug) => `/genre/${slug}`}
              />
            ))}
          </div>

          {moreError ? (
            <ErrorNote error={moreError} what="the next page of results" onRetry={loadMore} />
          ) : null}

          <div style={TAIL}>
            {remaining > 0 ? (
              <button
                type="button"
                className="btn-ghost"
                style={{ padding: '16px 28px', fontSize: '1rem' }}
                disabled={moreLoading}
                onClick={loadMore}
              >
                {moreLoading
                  ? 'Loading…'
                  : `Load ${Math.min(PAGE_SIZE, remaining)} more`}
              </button>
            ) : null}
            <span className="mono dim">
              Showing {items.length} of {total} · stream only, no downloads
            </span>
          </div>
        </>
      )}
    </>
  );
}
